import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { LanguageSwitch } from '../LanguageSwitch';
import { crumbFor } from '../nav';
import { useLayout } from '@/app/store/layout.store';
import { useSession } from '@/app/store/session.store';
import { NotificationsMenu } from '@/features/activity';
import { useUnreadCount } from '@/features/messaging';
import { authApi, endSession } from '@/features/auth';
import { Avatar } from '@/shared/ui/atoms/Avatar';
import { Icon } from '@/shared/ui/atoms/Icon';
import { SearchBox } from '@/shared/ui/molecules/SearchBox';
import './Topbar.styl';

type Props = { platform?: boolean; context?: string };

export function Topbar({ platform = false, context }: Props) {
  const user = useSession((s) => s.user);
  const { t } = useTranslation();
  const setSidebarOpen = useLayout((s) => s.setSidebarOpen);
  const location = useLocation();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [menu, setMenu] = useState(false);
  const unreadMessages = useUnreadCount();
  const menuRef = useRef<HTMLDivElement>(null);
  const logout = useMutation({
    mutationFn: authApi.logout,
    onSettled: () => {
      endSession();
      navigate('/login', { replace: true });
    }
  });

  useEffect(() => {
    if (!menu) return;
    const onDoc = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node))
        setMenu(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [menu]);

  const search = (e: FormEvent) => {
    e.preventDefault();
    const term = q.trim();
    navigate(term ? `/board?q=${encodeURIComponent(term)}` : '/board');
  };

  const crumb = crumbFor(location.pathname);
  const root = platform
    ? t('nav.platformRoot')
    : (user?.tenantName ?? 'Syncra');

  return (
    <header className="w-topbar">
      <button
        type="button"
        className="w-topbar__hamburger"
        onClick={() => setSidebarOpen(true)}
        aria-label={t('shell.openMenu')}
      >
        <Icon name="menu" size={20} strokeWidth={1.8} />
      </button>
      <nav className="w-topbar__crumbs" aria-label={t('nav.breadcrumb')}>
        <span>{root}</span>
        {context ? (
          <>
            <span className="w-topbar__sep">/</span>
            <span>{context}</span>
          </>
        ) : null}
        <span className="w-topbar__sep">/</span>
        <span className="w-topbar__crumb--current" aria-current="page">
          {t(`nav.${crumb}`)}
        </span>
      </nav>
      <div className="w-topbar__spacer" />
      {platform ? null : (
        <form onSubmit={search} role="search">
          <SearchBox
            placeholder={t('shell.searchPlaceholder')}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            aria-label={t('common.search')}
          />
        </form>
      )}
      {/* messages and notifications sit together, apart from the user's session */}
      <div className="w-topbar__icons">
        {platform ? null : (
          <button
            type="button"
            className="w-topbar__icon-btn"
            onClick={() => navigate('/messages')}
            aria-label={
              unreadMessages > 0
                ? t('shell.messagesUnread', { count: unreadMessages })
                : t('nav.messages')
            }
          >
            <Icon name="messages" size={15} />
            {unreadMessages > 0 ? (
              <span className="w-topbar__badge" aria-hidden="true">
                {unreadMessages > 99 ? '99+' : unreadMessages}
              </span>
            ) : null}
          </button>
        )}
        <NotificationsMenu />
      </div>
      <div className="w-topbar__user" ref={menuRef}>
        <button
          type="button"
          className="w-topbar__user-btn"
          onClick={() => setMenu((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={menu}
        >
          {user ? (
            <Avatar
              initials={user.initials}
              color={user.avatarColor}
              src={user.avatarUrl ?? null}
              size={36}
              name={user.displayName}
            />
          ) : null}
          <span
            className="w-topbar__user-text"
            title={t(`roles.${user?.role ?? 'MEMBER'}`)}
          >
            <span className="w-topbar__user-name">{user?.displayName}</span>
            <span className="w-topbar__user-role">
              {user?.roleName ?? t(`roles.${user?.role ?? 'MEMBER'}`)}
            </span>
          </span>
          <Icon name="caret" size={13} className="w-topbar__chevron" />
        </button>
        {menu ? (
          <div className="w-topbar__menu" role="menu">
            {platform ? null : (
              <button
                type="button"
                role="menuitem"
                className="w-topbar__menu-item"
                onClick={() => navigate('/settings?section=profile')}
              >
                <Icon name="profile" size={16} /> {t('shell.profile')}
              </button>
            )}
            <button
              type="button"
              role="menuitem"
              className="w-topbar__menu-item"
              onClick={() =>
                navigate(platform ? '/platform/settings' : '/settings')
              }
            >
              <Icon name="settings" size={16} /> {t('shell.settings')}
            </button>
            <button
              type="button"
              role="menuitem"
              className="w-topbar__menu-item w-topbar__menu-item--danger"
              onClick={() => logout.mutate()}
            >
              <Icon name="signOut" size={16} /> {t('shell.logout')}
            </button>
            <div className="w-topbar__menu-lang">
              <span>{t('common.language')}</span>
              <LanguageSwitch compact />
            </div>
          </div>
        ) : null}
      </div>
    </header>
  );
}
